import React, { useState, useRef, useEffect } from "react";
import ReactAudioPlayer from "react-audio-player";
import "./../components/AudioPlayer.css";
import pauseIcon from "./../../../assets/images/audioplayer/Group 1.svg";
import playIcon from "./../../../assets/images/audioplayer/Group 2.svg";
import replayIcon from "./../../../assets/images/audioplayer/Group 3.svg";

interface AudioPlayerProps {
  src: any;
  onPlay?: () => void;
  onPause?: () => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, onPlay, onPause }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0); // Default to 0
  const [hasEnded, setHasEnded] = useState(false);
  const audioPlayerRef = useRef<ReactAudioPlayer | null>(null);

  const togglePlayPause = () => {
    const audioEl = audioPlayerRef.current?.audioEl.current;
    if (audioEl) {
      if (isPlaying) {
        audioEl.pause();
        onPause?.();
      } else {
        audioEl.play();
        onPlay?.();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleReplay = () => {
    const audioEl = audioPlayerRef.current?.audioEl.current;
    if (audioEl) {
      audioEl.currentTime = 0;
      audioEl.play();
      setHasEnded(false);
      setIsPlaying(true);
    }
  };

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audioEl = audioPlayerRef.current?.audioEl.current;
    if (audioEl) {
      const newValue = parseFloat(event.target.value);
      audioEl.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const handleMetadataLoaded = () => {
    const audioEl = audioPlayerRef.current?.audioEl.current;
    if (audioEl) {
      setDuration(audioEl.duration);
    }
  };

  const handleAudioEnd = () => {
    setHasEnded(true);
    setIsPlaying(false);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      const audioEl = audioPlayerRef.current?.audioEl.current;
      if (audioEl) {
        setCurrentTime(audioEl.currentTime);
      }
    }, 100); // Update currentTime every 100ms

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="audio-player-container">
      <button
        onClick={hasEnded ? handleReplay : togglePlayPause}
        className="audio-player-button"
      >
        <img
          src={hasEnded ? replayIcon : isPlaying ? pauseIcon : playIcon}
          alt={hasEnded ? "Replay" : isPlaying ? "Pause" : "Play"}
          className="audio-player-icon"
        />
      </button>

      <span className="audio-time">{formatTime(currentTime)}</span>
      {/* <div className="audio-slider-container">
        <div
          className="audio-slider-played"
          style={{
            width: `${(currentTime / duration) * 97}%`, // Dynamically adjust played portion
          }}
        ></div>

        <input
          type="range"
          className="form-range audio-slider"
          min={0}
          max={duration}
          value={currentTime}
          onChange={handleSliderChange}
        />
      </div> */}

      <div className="audio-slider-container">
        {/* Played portion */}
        <div
          className="audio-slider-played"
          style={{
            width: `${(currentTime / duration) * 100}%`,
          }}
        ></div>

        {/* Full track background */}
        <div className="audio-slider-background"></div>

        {/* Custom thumb */}
        <div
          className="audio-slider-thumb"
          style={{
            left: `calc(${(currentTime / duration) * 100}% - 7.5px)`, // Position thumb
          }}
        ></div>

        {/* Invisible slider for interactivity */}
        <input
          type="range"
          className="audio-slider-transparent"
          min={0}
          max={duration}
          value={currentTime}
          onChange={handleSliderChange}
        />
      </div>

      <span className="audio-time">{formatTime(duration)}</span>

      <ReactAudioPlayer
        ref={audioPlayerRef}
        src={src}
        listenInterval={500}
        onLoadedMetadata={handleMetadataLoaded}
        onEnded={handleAudioEnd} // Handle audio end
      />
    </div>
  );
};

export default AudioPlayer;
